/** Sticky save bar (Saleor-style).
 *
 * Появляется внизу страницы когда форма dirty. Имеет:
 *  - текст «Несохранённые изменения»
 *  - Discard button — reset формы к исходным значениям
 *  - Save button — submit
 *
 * Использование:
 *   <form onSubmit={handleSubmit(save)}>
 *     ...поля...
 *     <StickySaveBar
 *       dirty={formState.isDirty}
 *       saving={mutation.isPending}
 *       onDiscard={() => reset()}
 *     />
 *   </form>
 *
 * Auto-warn при попытке покинуть страницу (beforeunload + react-router
 * blocker — пока только beforeunload, hash navigation отдельно).
 */

import { Loader2, RotateCcw, Save, X } from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  dirty: boolean;
  saving: boolean;
  /** Callback на Discard. Должен вызвать form.reset() с исходными values. */
  onDiscard?: () => void;
  /** Что показать слева когда dirty. Default — «Несохранённые изменения». */
  message?: string;
  /** Текст save button. Default — «Сохранить». */
  saveLabel?: string;
  /** Если форма submit'ится внешне (не type=submit) — обработчик save click. */
  onSave?: () => void;
  /** Скрывает Discard button если onDiscard не передан или это не нужно. */
  showDiscard?: boolean;
}

export function StickySaveBar({
  dirty,
  saving,
  onDiscard,
  message = "Несохранённые изменения",
  saveLabel = "Сохранить",
  onSave,
  showDiscard = true,
}: Props): JSX.Element | null {
  // beforeunload при попытке закрыть вкладку/обновить
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome требует returnValue
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  if (!dirty && !saving) return null;

  return (
    <>
      {/* Spacer чтобы content не закрывался bar'ом */}
      <div className="h-20" aria-hidden />

      <div
        className={cn(
          "fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur",
          "shadow-[0_-2px_8px_rgba(0,0,0,0.08)]",
        )}
        role="region"
        aria-label="Save bar"
      >
        <div className="container mx-auto flex items-center justify-between gap-3 px-4 py-3 max-w-screen-xl">
          <div className="flex items-center gap-2 text-sm">
            <span
              className="inline-block w-2 h-2 rounded-full bg-warning"
              aria-hidden
            />
            <span className="font-medium">{message}</span>
          </div>
          <div className="flex items-center gap-2">
            {showDiscard && onDiscard && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onDiscard}
                disabled={saving}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                Отменить
              </Button>
            )}
            {onSave ? (
              <Button type="button" size="sm" onClick={onSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1" />
                )}
                {saveLabel}
              </Button>
            ) : (
              <Button type="submit" size="sm" disabled={saving}>
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 mr-1" />
                )}
                {saveLabel}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/** Inline dismiss button — для notifications/banners в сheen save bar контекста. */
export function StickyBarDismiss({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      className="rounded-md p-1 hover:bg-accent"
      aria-label="Скрыть"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  );
}
